<template>
  <div>
    <!-- Header -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col cols="12" class="d-flex align-items-center justify-content-between">
            <div>
              <h4 class="mb-0">View Customer</h4>
              <small class="text-muted">Customer details and information</small>
            </div>
            <div>
              <b-button variant="outline-secondary" class="mr-1" @click="$router.go(-1)">
                <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
                Back
              </b-button>
              <b-button
                variant="outline-primary"
                class="mr-1"
                @click="$router.push(`/apps/fbr-invoice/customer/edit/${id}`)"
              >
                <feather-icon icon="EditIcon" size="16" class="mr-1" />
                Edit
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Loading Overlay -->
    <b-overlay :show="isLoading" spinner-variant="primary" rounded="sm">
      <!-- Customer Details Card -->
      <b-card class="mt-2">
        <div v-if="customerData" class="customer-details">
          <b-row>
            <!-- Basic Information -->
            <b-col cols="12" md="6">
              <h5 class="mb-3 text-primary">Basic Information</h5>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">ID:</label>
                <span class="detail-value">{{ customerData.id }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Code:</label>
                <span class="detail-value">{{ customerData.code || '-' }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Name:</label>
                <span class="detail-value">{{ customerData.name || '-' }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Short Name:</label>
                <span class="detail-value">{{ customerData.shortName || '-' }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Category:</label>
                <span class="detail-value">
                  <b-badge
                    :variant="customerData.category === 'Registered' ? 'primary' : 'secondary'"
                    class="text-uppercase"
                  >
                    {{ customerData.category || 'Unregistered' }}
                  </b-badge>
                </span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Status:</label>
                <span class="detail-value">
                  <b-badge
                    :variant="customerData.isActive ? 'success' : 'danger'"
                    class="text-uppercase"
                  >
                    {{ customerData.isActive ? 'Active' : 'Inactive' }}
                  </b-badge>
                </span>
              </div>
            </b-col>

            <!-- Contact Information -->
            <b-col cols="12" md="6">
              <h5 class="mb-3 text-primary">Contact Information</h5>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Cell Number:</label>
                <span class="detail-value">{{ customerData.cellNumber || '-' }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Email:</label>
                <span class="detail-value">{{ customerData.email || '-' }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Contact Person:</label>
                <span class="detail-value">{{ customerData.contactPerson || '-' }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Whatsapp:</label>
                <span class="detail-value">{{ customerData.whatsappOrUAN || '-' }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Website:</label>
                <span class="detail-value">{{ customerData.website || '-' }}</span>
              </div>
            </b-col>

            <!-- Business Information -->
            <b-col cols="12" md="6">
              <h5 class="mb-3 text-primary">Business Information</h5>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">NTN Number:</label>
                <span class="detail-value">{{ customerData.ntnNumber || '-' }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">STRN Number:</label>
                <span class="detail-value">{{ customerData.gstNumber || '-' }}</span>
              </div>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Commission %:</label>
                <span class="detail-value">{{
                  customerData.commissionPercent ? `${customerData.commissionPercent}%` : '-'
                }}</span>
              </div>
            </b-col>

            <!-- Address Information -->
            <b-col cols="12" md="6">
              <h5 class="mb-3 text-primary">Address Information</h5>

              <div class="detail-row mb-3">
                <label class="detail-label font-weight-bold">Address:</label>
                <span class="detail-value">{{ customerData.address || '-' }}</span>
              </div>
            </b-col>

            <!-- Audit Information -->
            <b-col cols="12">
              <h5 class="mb-3 text-primary">Audit Information</h5>

              <b-row>
                <b-col cols="12" md="6">
                  <div class="detail-row mb-3">
                    <label class="detail-label font-weight-bold">Added By:</label>
                    <span class="detail-value">{{
                      customerData.metaAddedBy || customerData.addedBy || '-'
                    }}</span>
                  </div>

                  <div class="detail-row mb-3">
                    <label class="detail-label font-weight-bold">Added On:</label>
                    <span class="detail-value">{{ formatDate(customerData.addedOn) }}</span>
                  </div>
                </b-col>

                <b-col cols="12" md="6">
                  <div class="detail-row mb-3">
                    <label class="detail-label font-weight-bold">Updated By:</label>
                    <span class="detail-value">{{
                      customerData.metaUpdatedBy || customerData.updatedBy || '-'
                    }}</span>
                  </div>

                  <div class="detail-row mb-3">
                    <label class="detail-label font-weight-bold">Updated On:</label>
                    <span class="detail-value">{{ formatDate(customerData.updatedOn) }}</span>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>

        <!-- No Data Message -->
        <div v-else-if="!isLoading" class="text-center text-muted">
          <feather-icon icon="AlertCircleIcon" size="48" class="mb-2" />
          <p>No customer data found</p>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BButton, BBadge, BOverlay } from 'bootstrap-vue';
import { ref, computed, onMounted } from '@vue/composition-api';
import store from '@/store';
import { api } from '@/utils/api';

export default {
  name: 'CustomerView',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BOverlay,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props) {
    const businessID = computed(() => store.state.user.businessID);
    const userID = computed(() => store.state.user.userID);

    // Loading state
    const isLoading = ref(false);
    const customerData = ref(null);

    const fetchCustomerData = async () => {
      if (!props.id) return;

      isLoading.value = true;
      try {
        console.log('=== props.id:', props.id);
        console.log('=== businessID:', businessID.value);
        console.log('=== userID:', userID.value);
        // Direct API call to LoadSelectedData
        const response = await api.get(
          `/CustomersFBR/LoadSelectedData/${businessID.value}?id=${props.id}&userID=${userID.value}`
        );

        console.log('=== CustomerView fetchCustomerData response:', response);

        // Handle different possible response structures
        let fetchedCustomer = null;

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.entity
        ) {
          fetchedCustomer = response.data.data.entity;
        } else if (response.data && response.data.entity) {
          fetchedCustomer = response.data.entity;
        } else if (response.data && response.data.id) {
          fetchedCustomer = response.data;
        } else {
          console.error('Unexpected response structure:', response);
          throw new Error('Invalid response structure from API');
        }

        customerData.value = fetchedCustomer;
        console.log('=== CustomerView populated data:', customerData.value);
      } catch (error) {
        console.error('Error fetching customer:', error);
        store.dispatch('app/showToast', {
          title: 'Error',
          text: 'Failed to load customer data',
          variant: 'danger',
        });
      } finally {
        isLoading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleString();
      } catch (error) {
        return dateString;
      }
    };

    const editCustomer = () => {
      this.$router.push(`/apps/fbr-invoice/customer/edit/${props.id}`);
    };

    // Fetch customer data when component mounts
    onMounted(() => {
      fetchCustomerData();
    });

    return {
      customerData,
      isLoading,
      formatDate,
      editCustomer,
    };
  },
};
</script>

<style lang="scss" scoped>
.customer-details {
  .detail-row {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1rem;

    .detail-label {
      min-width: 120px;
      margin-right: 1rem;
      color: #6c757d;
    }

    .detail-value {
      flex: 1;
      word-break: break-word;
    }
  }
}

@media (max-width: 768px) {
  .customer-details {
    .detail-row {
      flex-direction: column;
      align-items: flex-start;

      .detail-label {
        min-width: auto;
        margin-right: 0;
        margin-bottom: 0.25rem;
      }
    }
  }
}
</style>
